import { ref, computed, onUnmounted } from 'vue'
import { useErrorHandlerStore } from '@/stores/errorHandler'
import type { NetworkError } from '@/types/api'
import { ErrorHandler } from '@/utils/errorHandler'

/**
 * 错误处理组合函数选项
 */
export interface UseErrorHandlerOptions {
  context?: string
  autoRetry?: boolean
  maxRetries?: number
  logErrors?: boolean
}

/**
 * 错误处理组合函数
 * 提供统一的错误处理、加载状态管理和重试机制
 */
export function useErrorHandler(options: UseErrorHandlerOptions = {}) {
  const {
    context = 'default',
    autoRetry = false,
    maxRetries = 3,
    logErrors = true
  } = options

  const errorHandlerStore = useErrorHandlerStore()
  
  // 本地状态
  const localErrors = ref<string[]>([])
  const localLoading = ref<string[]>([])
  const retryAttempts = ref<Map<string, number>>(new Map())

  // 计算属性
  const hasLocalErrors = computed(() => localErrors.value.length > 0)
  const hasLocalLoading = computed(() => localLoading.value.length > 0)
  const isLoading = computed(() => hasLocalLoading.value || errorHandlerStore.globalLoading)

  const contextErrors = computed(() => 
    errorHandlerStore.errors.filter(error => error.context === context)
  )

  const contextLoading = computed(() => 
    errorHandlerStore.loadingStates.filter(loading => loading.context === context)
  )

  // 错误处理方法
  const handleError = (
    error: NetworkError | Error | any,
    retryFn?: () => Promise<any>
  ): string => {
    let networkError: NetworkError

    // 转换错误类型
    if (error instanceof Error) {
      networkError = ErrorHandler.createError('UNKNOWN_ERROR', error.message)
    } else if (error.type) {
      networkError = error as NetworkError
    } else {
      networkError = ErrorHandler.fromAxiosError(error)
    }

    // 添加到全局错误存储
    const errorId = errorHandlerStore.addError(networkError, context, retryFn)
    localErrors.value.push(errorId)

    // 记录日志
    if (logErrors) {
      ErrorHandler.logError(networkError, context)
    }

    // 自动重试逻辑
    if (autoRetry && retryFn && ErrorHandler.isNetworkError(networkError)) {
      const currentAttempts = retryAttempts.value.get(errorId) || 0
      if (currentAttempts < maxRetries) {
        retryAttempts.value.set(errorId, currentAttempts + 1)
        
        // 延迟重试（指数退避）
        const delay = Math.pow(2, currentAttempts) * 1000
        setTimeout(() => {
          retryError(errorId)
        }, delay)
      }
    }

    return errorId
  }

  const clearError = (errorId: string): void => {
    errorHandlerStore.removeError(errorId)
    const index = localErrors.value.indexOf(errorId)
    if (index > -1) {
      localErrors.value.splice(index, 1)
    }
    retryAttempts.value.delete(errorId)
  }

  const clearAllErrors = (): void => {
    localErrors.value.forEach(errorId => {
      errorHandlerStore.removeError(errorId)
    })
    localErrors.value = []
    retryAttempts.value.clear()
  }

  const retryError = async (errorId: string): Promise<boolean> => {
    try {
      const success = await errorHandlerStore.retryError(errorId)
      if (success) {
        clearError(errorId)
      }
      return success
    } catch (error) {
      console.error(`[useErrorHandler] 重试错误失败:`, error)
      return false
    }
  }

  // 加载状态管理
  const startLoading = (message: string = '加载中...'): string => {
    const loadingId = errorHandlerStore.startLoading(message, context)
    localLoading.value.push(loadingId)
    return loadingId
  }

  const updateLoading = (loadingId: string, message?: string, progress?: number): void => {
    errorHandlerStore.updateLoading(loadingId, message, progress)
  }

  const stopLoading = (loadingId: string): void => {
    errorHandlerStore.stopLoading(loadingId)
    const index = localLoading.value.indexOf(loadingId)
    if (index > -1) {
      localLoading.value.splice(index, 1)
    }
  }

  const clearAllLoading = (): void => {
    localLoading.value.forEach(loadingId => {
      errorHandlerStore.stopLoading(loadingId)
    })
    localLoading.value = []
  }

  // 异步操作包装器
  const withErrorHandling = async <T>(
    operation: () => Promise<T>,
    loadingMessage?: string,
    retryFn?: () => Promise<T>
  ): Promise<T | null> => {
    let loadingId: string | null = null

    try {
      // 开始加载
      if (loadingMessage) {
        loadingId = startLoading(loadingMessage)
      }

      // 执行操作
      const result = await operation()
      
      return result
    } catch (error) {
      // 处理错误
      handleError(error, retryFn || operation)
      return null
    } finally {
      // 停止加载
      if (loadingId) {
        stopLoading(loadingId)
      }
    }
  }

  // 带重试的异步操作包装器
  const withRetry = async <T>(
    operation: () => Promise<T>,
    options: {
      maxRetries?: number
      retryDelay?: number
      loadingMessage?: string
      retryCondition?: (error: any) => boolean
    } = {}
  ): Promise<T | null> => {
    const {
      maxRetries: localMaxRetries = 3,
      retryDelay = 1000,
      loadingMessage,
      retryCondition = (error) => ErrorHandler.isNetworkError(error)
    } = options

    let loadingId: string | null = null
    let lastError: any = null

    try {
      if (loadingMessage) {
        loadingId = startLoading(loadingMessage)
      }

      for (let attempt = 0; attempt <= localMaxRetries; attempt++) {
        try {
          const result = await operation()
          return result
        } catch (error) {
          lastError = error
          
          // 检查是否应该重试
          if (attempt < localMaxRetries && retryCondition(error)) {
            console.log(`[useErrorHandler] 操作失败，${retryDelay}ms 后重试 (${attempt + 1}/${localMaxRetries})`)
            
            // 更新加载消息
            if (loadingId) {
              updateLoading(loadingId, `${loadingMessage} (重试 ${attempt + 1}/${localMaxRetries})`)
            }
            
            // 等待重试延迟
            await new Promise(resolve => setTimeout(resolve, retryDelay * Math.pow(2, attempt)))
          } else {
            throw error
          }
        }
      }
    } catch (error) {
      handleError(lastError || error)
      return null
    } finally {
      if (loadingId) {
        stopLoading(loadingId)
      }
    }

    return null
  }

  // 网络状态检查
  const checkNetworkAndRetry = async (): Promise<void> => {
    if (!errorHandlerStore.networkStatus.isOnline) {
      console.warn('[useErrorHandler] 网络离线，无法重试')
      return
    }

    // 重试所有网络相关错误
    const networkErrorIds = contextErrors.value
      .filter(error => ErrorHandler.isNetworkError(error) && error.retryable)
      .map(error => error.id)

    for (const errorId of networkErrorIds) {
      await retryError(errorId)
    }
  }

  // 获取错误统计
  const getErrorStats = () => ({
    total: localErrors.value.length,
    byType: localErrors.value.reduce((acc, errorId) => {
      const error = errorHandlerStore.getErrorById(errorId)
      if (error) {
        acc[error.type] = (acc[error.type] || 0) + 1
      }
      return acc
    }, {} as Record<string, number>),
    retryable: localErrors.value.filter(errorId => {
      const error = errorHandlerStore.getErrorById(errorId)
      return error?.retryable
    }).length
  })

  // 清理函数
  const cleanup = (): void => {
    clearAllErrors()
    clearAllLoading()
    retryAttempts.value.clear()
  }

  // 组件卸载时清理
  onUnmounted(() => {
    cleanup()
  })

  return {
    // 状态
    hasLocalErrors,
    hasLocalLoading,
    isLoading,
    contextErrors,
    contextLoading,
    
    // 错误处理
    handleError,
    clearError,
    clearAllErrors,
    retryError,
    
    // 加载状态
    startLoading,
    updateLoading,
    stopLoading,
    clearAllLoading,
    
    // 高级功能
    withErrorHandling,
    withRetry,
    checkNetworkAndRetry,
    
    // 工具方法
    getErrorStats,
    cleanup
  }
}

/**
 * 全局错误处理组合函数
 * 用于处理全局级别的错误和加载状态
 */
export function useGlobalErrorHandler() {
  const errorHandlerStore = useErrorHandlerStore()

  const showGlobalError = (
    message: string,
    type: 'error' | 'warning' | 'info' = 'error'
  ): void => {
    // 这里可以集成全局通知组件
    console.log(`[GlobalError] ${type.toUpperCase()}: ${message}`)
    
    // 如果是错误类型，添加到错误存储
    if (type === 'error') {
      errorHandlerStore.addError(
        ErrorHandler.createError('UNKNOWN_ERROR', message),
        'global'
      )
    }
  }

  const showGlobalLoading = (show: boolean, message?: string): void => {
    errorHandlerStore.setGlobalLoading(show)
    if (show && message) {
      console.log(`[GlobalLoading] ${message}`)
    }
  }

  return {
    showGlobalError,
    showGlobalLoading,
    ...errorHandlerStore
  }
}